import React from "react";
import { Button, Flex } from "theme-ui";

import {
  Decimal,
  Decimalish,
  LiquityStoreState,
  LQTYStake,
  LQTYStakeChange
} from "@liquity/lib-base";

import { LiquityStoreUpdate, useLiquityReducer, useLiquitySelector } from "@liquity/lib-react";

import { GT, COIN } from "../../strings";

import { useStakingView } from "./context/StakingViewContext";
import { StakingEditor } from "./StakingEditor";
import { StakingManagerAction } from "./StakingManagerAction";
import { ActionDescription, Amount } from "../ActionDescription";
import { ErrorDescription } from "../ErrorDescription";

const init = ({ lqtyStake }: LiquityStoreState) => ({
  originalStake: lqtyStake,
  editedLQTY: Decimal.ZERO
});

type StakeManagerState = ReturnType<typeof init>;
type StakeManagerAction =
  | LiquityStoreUpdate
  | { type: "revert" }
  | { type: "setStake"; newValue: Decimalish };

interface StakingManagerProps {
    mode?: "stake" | "redeem";
  }

const reduce = (state: StakeManagerState, action: StakeManagerAction): StakeManagerState => {

  const { originalStake, editedLQTY } = state;

  switch (action.type) {
    case "setStake":
      return { ...state, editedLQTY: Decimal.from(action.newValue) };

    case "revert":
      return { ...state, editedLQTY: Decimal.ZERO };

    case "updateStore": {
      const {
        stateChange: { lqtyStake: updatedStake }
      } = action;

      if (updatedStake) {
        return {
          originalStake: updatedStake,
          editedLQTY: updatedStake.apply(originalStake.whatChanged(editedLQTY))
        };
      }
    }
  }

  return state;
};

const selectLQTYBalance = ({ lqtyBalance }: LiquityStoreState) => lqtyBalance;
const selectsPRINTBalance = ({ sPrintBalance }: LiquityStoreState) => sPrintBalance;

type StakingManagerActionDescriptionProps = {
  originalStake: LQTYStake;
  change: Decimal;
  mode: string;
};

const StakingManagerActionDescription: React.FC<StakingManagerActionDescriptionProps> = ({
  originalStake,
  change,
  mode
}) => {
  const stakeLQTY = change?.prettify().concat(" ", GT);

  // if (originalStake.isEmpty && stakeLQTY) {
    return (
      <ActionDescription>
        You are { mode == "stake" ? " staking" : "redeeming"} <Amount>{stakeLQTY}</Amount>.
      </ActionDescription>
    );
  // }

  return (
    <ActionDescription>
      {stakeLQTY && (
        <>
          You are adding <Amount>{stakeLQTY}</Amount> to your stake
        </>
      )}
      {/* {unstakeLQTY && (
        <>
          You are withdrawing <Amount>{unstakeLQTY}</Amount> to your wallet
        </>
      )} */}
      {/* {(collateralGain || lusdGain) && (
        <>
          {" "}
          and claiming{" "}
          {collateralGain && lusdGain ? (
            <>
              <Amount>{collateralGain}</Amount> and <Amount>{lusdGain}</Amount>
            </>
          ) : (
            <>
              <Amount>{collateralGain ?? lusdGain}</Amount>
            </>
          )}
        </>
      )} */}
      .
    </ActionDescription>
  );
};

export const StakingManager: React.FC<StakingManagerProps> = ({ mode = "stake"}) => {
  const { dispatch: dispatchStakingViewAction } = useStakingView();
  const [{ originalStake, editedLQTY }, dispatch] = useLiquityReducer(reduce, init);
  const lqtyBalance = useLiquitySelector(selectLQTYBalance);
  const sPrintBalance = useLiquitySelector(selectsPRINTBalance);
  const message = mode == "stake" ? `Enter the amount of ${GT} you'd like to stake.` : `Enter the amount of sPrint you'd like to redeem.`;
  const balance = mode == "stake" ? lqtyBalance : sPrintBalance;

  // const change = originalStake.whatChanged(editedLQTY);
  const change = editedLQTY;
  const [validChange, description] = !change
    ? [undefined, undefined]
    : change?.gt(balance)
    ? [
        undefined,
        <ErrorDescription>
          {`The amount you're trying to ${mode} exceeds your balance by `}
          <Amount>
            {change.sub(balance).prettify()} {GT}
          </Amount>
          .
        </ErrorDescription>
      ]
    : [change, <StakingManagerActionDescription originalStake={originalStake} change={change} mode={mode} />];
  const makingNewStake = originalStake.isEmpty;
// editedLQTY = Decimal.ZERO;

  return (
    <StakingEditor title={"Liquid Staked PRINT (sPRINT)"} {...{ originalStake, editedLQTY, mode, dispatch }}>
      {description ??
        (makingNewStake ? (
          <ActionDescription>{ message }</ActionDescription>
        ) : (
          <ActionDescription>Adjust the {GT} amount to stake or withdraw.</ActionDescription>
        ))}

      <Flex variant="layout.actions">
        <Button
          variant="cancel"
          onClick={() => dispatchStakingViewAction({ type: "cancelAdjusting" })}
        >
          Cancel
        </Button>

        {validChange ? (
          <StakingManagerAction change={validChange} sprintType={mode}>Confirm</StakingManagerAction>
        ) : (
          <Button disabled>Confirm</Button>
        )}
      </Flex>
    </StakingEditor>
  );
};
